import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiDatabase, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { authenticatedFetch } from '../utils/api';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  justify-content: center;
  overflow-y: auto;
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const DetailsSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 3px;
`;

const DetailValue = styled.div`
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  
  > span {
    flex: 1;
    min-width: 0;
  }
  
  > button {
    flex-shrink: 0;
  }
`;

const EditableInput = styled.input`
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
  border: 2px solid #9ca3af;
  border-radius: 6px;
  padding: 4px 8px;
  flex: 1;
  background: white;
`;

const EditButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: 1px solid #2563eb;
  border-radius: 6px;
  color: white;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border-color: #1d4ed8;
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  background: white;
  border: 1px solid #10b981;
  border-radius: 6px;
  color: #10b981;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f0fdf4;
    border-color: #059669;
    color: #059669;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #999;
  }
`;

const CancelButton = styled.button`
  background: white;
  border: 1px solid #ef4444;
  border-radius: 6px;
  color: #ef4444;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #f5f5f5;
    border-color: #e0e0e0;
    color: #999;
  }
`;

const InteractiveLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

const LoadingProgress = () => {
  return (
    <InteractiveLoadingContainer>
      <LoadingSpinner />
      <LoadingText>Loading Environment Details</LoadingText>
    </InteractiveLoadingContainer>
  );
};

const ErrorMessage = styled.div`
  text-align: center;
  color: #ef4444;
  padding: 40px;
  font-size: 1.1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PlantDetailPage = ({ plant, onBack }) => {
  const [environmentDetails, setEnvironmentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlantName, setEditingPlantName] = useState(false);
  const [plantNameValue, setPlantNameValue] = useState('');
  const [editingSlope, setEditingSlope] = useState(false);
  const [slopeValue, setSlopeValue] = useState('');
  const [editingFacing, setEditingFacing] = useState(false);
  const [facingValue, setFacingValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEnvironmentDetails();
  }, [plant.id]);

  const fetchEnvironmentDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/plants/${plant.id}/environment/details`);
      const data = await response.json();
      
      if (data.success && data.details) {
        setEnvironmentDetails(data.details);
        // Set initial plant name value (use PLANT_NAME from file, or fallback to PLANT_CODE or plant.id)
        const initialPlantName = data.details['PLANT_NAME'] || data.details['plant_name'] || data.details['PLANT_CODE'] || plant.id;
        setPlantNameValue(initialPlantName);
        // Set initial slope and facing values
        setSlopeValue(data.details['slope'] || '');
        setFacingValue(data.details['facing'] || '');
      } else if (data.error) {
        setError(data.error);
      } else {
        setError('Environment details not found');
      }
    } catch (err) {
      setError('Failed to fetch environment details');
    } finally {
      setLoading(false);
    }
  };

  // Format key names for display
  const formatKey = (key) => {
    if (key === 'LAST_DAY') {
      return 'First Day of Production';
    }
    if (key === 'PLANT_NAME') {
      return 'Plant Name';
    }
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/Id\b/g, 'ID');
  };

  // Get display order and grouping for environment details
  const getDisplayDetails = () => {
    if (!environmentDetails) return [];
    
    const orderedKeys = [
      'PLANT_CODE',
      'VERSION',
      'INVERTERS',
      'latitude',
      'longitude',
      'slope',
      'facing',
      'ENERGIA_PRODOTTA_ID',
      'IRRAGGIAMENTO_MODULI_ID',
      'TEMPERATURA_AMBIENTE_ID',
      'LAST_DAY'
    ];
    
    const details = [];
    
    // Add PLANT_CODE first
    if (environmentDetails.hasOwnProperty('PLANT_CODE')) {
      details.push({ key: 'PLANT_CODE', value: environmentDetails['PLANT_CODE'] });
    }
    
    // Add Plant Name entry (use PLANT_NAME from file if exists, otherwise use PLANT_CODE or plant.id)
    const plantNameValue = environmentDetails['PLANT_NAME'] || environmentDetails['plant_name'] || environmentDetails['PLANT_CODE'] || plant.id;
    details.push({ key: 'PLANT_NAME', value: plantNameValue });
    
    // Add remaining ordered keys
    for (const key of orderedKeys) {
      if (key !== 'PLANT_CODE' && key !== 'PLANT_NAME' && environmentDetails.hasOwnProperty(key)) {
        details.push({ key, value: environmentDetails[key] });
      }
    }
    
    // Add any remaining keys not in the ordered list (excluding PLANT_NAME which we already added)
    for (const key in environmentDetails) {
      if (!orderedKeys.includes(key) && key !== 'PLANT_NAME' && key !== 'plant_name') {
        details.push({ key, value: environmentDetails[key] });
      }
    }
    
    return details;
  };

  const handleEditPlantName = () => {
    const currentPlantName = environmentDetails?.['PLANT_NAME'] || environmentDetails?.['plant_name'] || environmentDetails?.['PLANT_CODE'] || plant.id;
    setPlantNameValue(currentPlantName);
    setEditingPlantName(true);
  };

  const handleSavePlantName = async () => {
    setSaving(true);
    try {
      const response = await authenticatedFetch(`/api/plants/${plant.id}/environment/plant-name`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plantName: plantNameValue }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh environment details to get the updated file
        await fetchEnvironmentDetails();
        setEditingPlantName(false);
      } else {
        alert('Failed to save plant name: ' + (data.error || 'Unknown error'));
      }
      } catch (err) {
        alert('Failed to save plant name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    const currentPlantName = environmentDetails?.['PLANT_NAME'] || environmentDetails?.['plant_name'] || environmentDetails?.['PLANT_CODE'] || plant.id;
    setPlantNameValue(currentPlantName);
    setEditingPlantName(false);
  };

  const handleEditSlope = () => {
    const currentSlope = environmentDetails?.['slope'] || '';
    setSlopeValue(currentSlope);
    setEditingSlope(true);
  };

  const handleSaveSlope = async () => {
    setSaving(true);
    try {
      const response = await authenticatedFetch(`/api/plants/${plant.id}/environment/slope`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slope: slopeValue }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh environment details to get the updated file
        await fetchEnvironmentDetails();
        setEditingSlope(false);
      } else {
        alert('Failed to save slope: ' + (data.error || 'Unknown error'));
      }
      } catch (err) {
        alert('Failed to save slope');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditSlope = () => {
    const currentSlope = environmentDetails?.['slope'] || '';
    setSlopeValue(currentSlope);
    setEditingSlope(false);
  };

  const handleEditFacing = () => {
    const currentFacing = environmentDetails?.['facing'] || '';
    setFacingValue(currentFacing);
    setEditingFacing(true);
  };

  const handleSaveFacing = async () => {
    setSaving(true);
    try {
      const response = await authenticatedFetch(`/api/plants/${plant.id}/environment/facing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ facing: facingValue }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh environment details to get the updated file
        await fetchEnvironmentDetails();
        setEditingFacing(false);
      } else {
        alert('Failed to save facing: ' + (data.error || 'Unknown error'));
      }
      } catch (err) {
        alert('Failed to save facing');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditFacing = () => {
    const currentFacing = environmentDetails?.['facing'] || '';
    setFacingValue(currentFacing);
    setEditingFacing(false);
  };

  return (
    <PageContainer>
      <MainContent>
        <ContentWrapper>
          <PageHeader>
            <PageTitle>Plant Details - {plant.id}</PageTitle>
          </PageHeader>

          <DetailsSection>
            
            {loading && (
              <LoadingProgress />
            )}

            {error && !loading && (
              <ErrorMessage>{error}</ErrorMessage>
            )}

            {!loading && !error && environmentDetails && (
              <DetailsGrid>
                {getDisplayDetails().map((item, index) => {
                  const isPlantName = item.key === 'PLANT_NAME';
                  const isSlope = item.key === 'slope';
                  const isFacing = item.key === 'facing';
                  
                  let displayValue = item.value;
                  if (isPlantName && editingPlantName) {
                    displayValue = plantNameValue;
                  } else if (isSlope && editingSlope) {
                    displayValue = slopeValue;
                  } else if (isFacing && editingFacing) {
                    displayValue = facingValue;
                  }
                  
                  return (
                    <DetailItem key={index}>
                      <DetailLabel>{formatKey(item.key)}</DetailLabel>
                      {isPlantName ? (
                        <DetailValue>
                          {editingPlantName ? (
                            <>
                              <EditableInput
                                type="text"
                                value={plantNameValue}
                                onChange={(e) => setPlantNameValue(e.target.value)}
                                disabled={saving}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <SaveButton onClick={handleSavePlantName} disabled={saving}>
                                  <FiCheck size={14} />
                                  Save
                                </SaveButton>
                                <CancelButton onClick={handleCancelEdit} disabled={saving}>
                                  <FiX size={14} />
                                  Cancel
                                </CancelButton>
                              </div>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1 }}>{displayValue}</span>
                              <EditButton onClick={handleEditPlantName}>
                                <FiEdit2 size={14} />
                                Edit
                              </EditButton>
                            </>
                          )}
                        </DetailValue>
                      ) : isSlope ? (
                        <DetailValue>
                          {editingSlope ? (
                            <>
                              <EditableInput
                                type="text"
                                value={slopeValue}
                                onChange={(e) => setSlopeValue(e.target.value)}
                                disabled={saving}
                                style={{ flex: 1, minWidth: 0 }}
                              />
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <SaveButton onClick={handleSaveSlope} disabled={saving}>
                                  <FiCheck size={14} />
                                  Save
                                </SaveButton>
                                <CancelButton onClick={handleCancelEditSlope} disabled={saving}>
                                  <FiX size={14} />
                                  Cancel
                                </CancelButton>
                              </div>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1 }}>{displayValue}</span>
                              <EditButton onClick={handleEditSlope}>
                                <FiEdit2 size={14} />
                                Edit
                              </EditButton>
                            </>
                          )}
                        </DetailValue>
                      ) : isFacing ? (
                        <DetailValue>
                          {editingFacing ? (
                            <>
                              <EditableInput
                                type="text"
                                value={facingValue}
                                onChange={(e) => setFacingValue(e.target.value)}
                                disabled={saving}
                              />
                              <SaveButton onClick={handleSaveFacing} disabled={saving}>
                                <FiCheck size={14} />
                                Save
                              </SaveButton>
                              <CancelButton onClick={handleCancelEditFacing} disabled={saving}>
                                <FiX size={14} />
                                Cancel
                              </CancelButton>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1 }}>{displayValue}</span>
                              <EditButton onClick={handleEditFacing}>
                                <FiEdit2 size={14} />
                                Edit
                              </EditButton>
                            </>
                          )}
                        </DetailValue>
                      ) : (
                        <DetailValue>{displayValue}</DetailValue>
                      )}
                    </DetailItem>
                  );
                })}
              </DetailsGrid>
            )}
          </DetailsSection>
        </ContentWrapper>
      </MainContent>
    </PageContainer>
  );
};

export default PlantDetailPage;
